// src/apiUtils/objectUtils.js

/**
 * Safely gets a value from a nested object path
 * @param {Object} obj - The object to traverse
 * @param {String} path - Dot notation path (e.g. "user.address.city")
 * @param {*} defaultValue - Optional default value if path not found
 * @returns {*} The value at path or defaultValue/undefined if not found
 */
export const getValueByPath = (obj, path, defaultValue = undefined) => {
  if (!obj || !path) return defaultValue;
  
  try {
    // Handle dot notation paths and convert to array
    const parts = typeof path === 'string' 
      ? path.replace(/\[(\w+)\]/g, '.$1').split('.') 
      : path;
    
    // Navigate through the object
    let current = obj;
    for (const part of parts) {
      if (part === '') continue; // Skip empty parts
      
      // Stop if we hit null/undefined or the property doesn't exist
      if (current === null || current === undefined || !(part in current)) {
        return defaultValue;
      }
      
      current = current[part];
    }
    
    return current;
  } catch (error) {
    console.error(`[getValueByPath] Error traversing path '${path}':`, error);
    return defaultValue;
  }
};


export const tryExtractFromMultipleLocations = (data, path) => {
  // Common locations to check, in order of priority
  const locationsToCheck = [
    { obj: data.response?.data, prefix: '' },
    { obj: data.node?.response?.data, prefix: '' },
    { obj: data.response, prefix: '' },
    { obj: data, prefix: '' }
  ];
  
  for (const location of locationsToCheck) {
    if (!location.obj) continue;
    
    const value = getValueByPath(location.obj, path);
    if (value !== undefined) return value;
  }
  
  return undefined;
};


// Helper function to resolve template strings with path variables
// export const resolveTemplateString = (templateString, extractedValues, data) => {
//   // If not a string or doesn't contain template markers, return as is
//   if (typeof templateString !== 'string' || !templateString.includes('{')) {
//     return templateString;
//   }
  
//   try {
//     // Replace all instances of {path.to.value} with their extracted values
//     return templateString.replace(/\{([^}]+)\}/g, (match, path) => {
//       // First check if we already extracted this value
//       if (extractedValues[path] !== undefined) {
//         return extractedValues[path];
//       }
      
//       // Try to extract directly from data
//       const value = tryExtractFromMultipleLocations(data, path);
//       if (value === undefined) {
//         console.warn(`Could not resolve template variable '${path}' in string: ${templateString}`);
//         return match; // Keep the original {path} if value not found
//       }
      
//       // Store for future reference
//       extractedValues[path] = value;
      
//       // Handle different value types
//       return typeof value === 'object' ? JSON.stringify(value) : String(value);
//     });
//   } catch (error) {
//     console.error(`Error resolving template string: ${templateString}`, error);
//     return templateString; // Return original on error
//   }
// };


export const resolveTemplateString = (templateString, extractedValues, data) => {
  if (typeof templateString !== 'string' || !templateString.includes('{')) {
    return templateString;
  }
  
  try {
    return templateString.replace(/\{([^}]+)\}/g, (match, path) => {
      // First check existing extracted values
      if (extractedValues[path] !== undefined) {
        return extractedValues[path];
      }
      
      // Try to extract directly
      let value = tryExtractFromMultipleLocations(data, path);
      
      // If not found and path starts with "data.", try without it
      if (value === undefined && path.startsWith('data.')) {
        const altPath = path.substring(5); // Remove "data." prefix
        value = tryExtractFromMultipleLocations(data, altPath);
      }
      
      if (value === undefined) {
        console.warn(`Could not resolve template variable '${path}' in string: ${templateString}`);
        return match;
      }
      
      // Store for future reference
      extractedValues[path] = value;
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    });
  } catch (error) {
    console.error(`Error resolving template string: ${templateString}`, error);
    return templateString;
  }
};